import { useCallback, useMemo, useState } from "react";
import { Box, Button, Flex, Group, Text, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconBookmark, IconRosetteDiscountCheckFilled } from "@tabler/icons-react";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { type CompanyWithUI } from "@/components/shared/CompanyCardHorizontal";
import TableRowLinkOverlay from "@/components/shared/TableRowLinkOverlay";
import TableRowLinkTableStyles from "@/components/shared/TableRowLinkTableStyles";
import { getIndustryLabel } from "@/components/shared/constants/general.const";
import ShortlistModal from "@/components/shared/UserSignupModal";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import { formatCompanyProfileTextForDisplay } from "@/shared/utils/capitalizeLeadingCharacter";
import { buildCompanyDiscoverPath, companyProfileAriaLabel } from "@/shared/utils/companyProfileLink.util";
import { useAuth } from "@/hooks/useAuth";

/** Desktop: column share of total width (must sum to 100%). */
const DISCOVER_COLUMN_WIDTH: Record<string, string> = {
  company: "17%",
  description: "39%",
  industry: "16%",
  location: "13%",
  actions: "15%",
};

/** Mobile: company, industry, country only. */
const DISCOVER_COLUMN_WIDTH_MOBILE: Record<string, string> = {
  company: "32%",
  industry: "38%",
  location: "30%",
};

/** Fits GTID + verified badge without clipping (desktop and mobile). */
const COMPANY_COLUMN_MIN_WIDTH_PX = 128;

const mobileCellPadding = (columnId: string): { paddingLeft?: number; paddingRight?: number } => {
  switch (columnId) {
    case "company":
      return { paddingLeft: 8, paddingRight: 12 };
    case "industry":
      return { paddingLeft: 28, paddingRight: 10 };
    case "location":
      return { paddingLeft: 8, paddingRight: 16 };
    default:
      return { paddingLeft: 4, paddingRight: 4 };
  }
};

const MOBILE_HIDDEN_COLUMN_IDS = new Set(["description", "actions"]);

const DISCOVER_BOOKMARK_BTN_STYLES = {
  root: {
    backgroundColor: "#04305c",
    color: "white",
    border: "1px solid #04305c",
    "&:hover": {
      backgroundColor: "#032847",
      borderColor: "#032847",
    },
  },
};

export type DiscoverCompaniesTableProps = {
  companies: CompanyWithUI[];
  /** Query string appended to `/discover/:id` links (e.g. `?mode=ai&keyword=...`). */
  profileSearch?: string;
  /** Passed through link state so back navigation restores discover filters. */
  discoverReturnTo?: string;
  canViewCompanyDetails?: boolean;
  onBlockedViewCompanyDetails?: () => void;
  onViewDetails: (companyId: string) => void;
  onShortlistClick: (companyId: string, isShortlisted: boolean) => void | Promise<void>;
  onGuestShortlistClick?: (companyId: string) => void;
};

export default function DiscoverCompaniesTable({
  companies,
  profileSearch = "",
  discoverReturnTo,
  canViewCompanyDetails = true,
  onBlockedViewCompanyDetails,
  onViewDetails,
  onShortlistClick,
  onGuestShortlistClick,
}: DiscoverCompaniesTableProps) {
  const theme = useMantineTheme();
  const isMobileTable = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`, false, {
    getInitialValueInEffect: true,
  });
  const { isAuthenticated } = useAuth();
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [signupCompanyLabel, setSignupCompanyLabel] = useState("");

  const columnVisibility = useMemo(
    () => ({
      description: !isMobileTable,
      actions: !isMobileTable,
    }),
    [isMobileTable],
  );

  const columnWidthFor = useCallback(
    (columnId: string) => {
      if (isMobileTable) {
        return DISCOVER_COLUMN_WIDTH_MOBILE[columnId] ?? DISCOVER_COLUMN_WIDTH[columnId];
      }
      return DISCOVER_COLUMN_WIDTH[columnId];
    },
    [isMobileTable],
  );

  const handleViewCompany = useCallback(
    (companyId: string) => {
      if (!canViewCompanyDetails) {
        onBlockedViewCompanyDetails?.();
        return;
      }
      onViewDetails(companyId);
    },
    [canViewCompanyDetails, onBlockedViewCompanyDetails, onViewDetails],
  );

  const columns = useMemo<MRT_ColumnDef<CompanyWithUI>[]>(
    () => [
      {
        id: "company",
        header: "Company",
        size: 160,
        Cell: ({ row }) => {
          const company = row.original;
          const companyLabel = company.gtid || "N/A";
          const content = (
            <Group
              gap={isMobileTable ? 6 : 8}
              wrap="nowrap"
              align="center"
              style={{ overflow: "visible", width: "fit-content", maxWidth: "none" }}
            >
              <Text
                component="span"
                size={isMobileTable ? "xs" : "sm"}
                fw={600}
                c="#04305c"
                style={{ flexShrink: 0, lineHeight: 1.3 }}
              >
                {companyLabel}
              </Text>
              {company.status === "VERIFIED" && (
                <Box component="span" style={{ flexShrink: 0, display: "inline-flex", lineHeight: 0 }}>
                  <IconRosetteDiscountCheckFilled
                    size={isMobileTable ? 15 : 16}
                    style={{ color: "var(--mantine-color-primary-6)", display: "block" }}
                  />
                </Box>
              )}
            </Group>
          );
          if (!canViewCompanyDetails) return content;
          return (
            <>
              <TableRowLinkOverlay
                to={buildCompanyDiscoverPath(company.id, profileSearch)}
                linkState={discoverReturnTo ? { discoverReturnTo } : undefined}
                ariaLabel={companyProfileAriaLabel(company.gtid, companyLabel)}
              />
              {content}
            </>
          );
        },
      },
      {
        id: "description",
        header: "Description",
        size: 400,
        Cell: ({ row }) => {
          const c = row.original;
          const raw = (c as { subDescription?: string }).subDescription ?? c.description;
          const content = (
            <Text size="sm" c="dark.7" lh={1.55} style={{ wordBreak: "break-word" }}>
              {formatCompanyProfileTextForDisplay(raw)}
            </Text>
          );
          return content;
        },
      },
      {
        id: "industry",
        header: "Industry",
        size: 140,
        Cell: ({ row }) => {
          const company = row.original;
          const cats = company.industryCategory;
          const content =
            !Array.isArray(cats) || cats.length === 0 ? (
              <Text size="sm" c="dimmed">
                —
              </Text>
            ) : (
              <Text
                size={isMobileTable ? "xs" : "sm"}
                lh={1.5}
                lineClamp={isMobileTable ? undefined : 2}
                style={{ wordBreak: "break-word", whiteSpace: "normal" }}
              >
                {getIndustryLabel(cats[0])}
                {cats.length > 1 ? ` +${cats.length - 1}` : ""}
              </Text>
            );
          return content;
        },
      },
      {
        id: "location",
        header: isMobileTable ? "Country" : "Location",
        size: 120,
        Cell: ({ row }) => {
          const company = row.original;
          const loc = company.headquartersCountry;
          const flag = flagEmojiFromHeadquartersCountry(company.headquartersCountry);
          const content = (
            <Flex
              direction="column"
              gap={6}
              align={isMobileTable ? "flex-end" : "flex-start"}
              w="100%"
              maw="100%"
              style={{ minWidth: 0 }}
            >
              {flag ? (
                <Text
                  component="span"
                  fz={isMobileTable ? 18 : 20}
                  lh={1}
                  style={{ flexShrink: 0, lineHeight: 1 }}
                  role="img"
                  aria-label={`${loc ?? "Country"} flag`}
                >
                  {flag}
                </Text>
              ) : null}
              <Text
                component="span"
                size={isMobileTable ? "xs" : "sm"}
                c={loc ? undefined : "dimmed"}
                lh={isMobileTable ? 1.35 : 1.4}
                ta={isMobileTable ? "right" : "left"}
                w="100%"
                maw="100%"
                style={{
                  minWidth: 0,
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  whiteSpace: "normal",
                }}
              >
                {loc || "—"}
              </Text>
            </Flex>
          );
          return content;
        },
      },
      {
        id: "actions",
        header: isMobileTable ? "" : "Shortlist",
        size: 56,
        enableSorting: false,
        mantineTableHeadCellProps: { align: "center" },
        mantineTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          const company = row.original;
          const handleBookmark = () => {
            if (!isAuthenticated) {
              onGuestShortlistClick?.(company.id);
              setSignupCompanyLabel(company.gtid ? `${company.gtid}` : (company.legalName ?? "this company"));
              setSignupModalOpen(true);
              return;
            }
            void onShortlistClick(company.id, !company.isShortlisted);
          };

          return (
            <Flex justify="center" align="center" w="100%" pos="relative" style={{ zIndex: 2 }}>
              <Button
                variant="filled"
                size={isMobileTable ? "compact-sm" : "sm"}
                radius="md"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBookmark();
                }}
                px="xs"
                styles={DISCOVER_BOOKMARK_BTN_STYLES}
                aria-label={company.isShortlisted ? "Remove from shortlist" : "Add to shortlist"}
              >
                <IconBookmark size={18} color="white" fill={company.isShortlisted ? "currentColor" : "none"} />
              </Button>
            </Flex>
          );
        },
      },
    ],
    [canViewCompanyDetails, discoverReturnTo, isAuthenticated, isMobileTable, onGuestShortlistClick, onShortlistClick, profileSearch],
  );

  return (
    <>
      <Box w="100%" maw="100%" className="gt-table-row-link" style={{ minWidth: 0, overflow: "hidden" }}>
        {canViewCompanyDetails ? <TableRowLinkTableStyles /> : null}
        <MantineReactTable
          columns={columns}
          data={companies}
          layoutMode="semantic"
          state={{ columnVisibility }}
          enablePagination={false}
          enableBottomToolbar={false}
          enableTopToolbar={false}
          enableColumnActions={false}
          enableSorting={false}
          enableGlobalFilter={false}
          enableColumnFilters={false}
          mantineTableProps={{ style: { width: "100%", tableLayout: "fixed" } }}
          mantineTableContainerProps={{
            style: { minHeight: 280, width: "100%", maxWidth: "100%", overflowX: "hidden" },
          }}
          mantineTableHeadCellProps={({ column }) => {
            if (MOBILE_HIDDEN_COLUMN_IDS.has(column.id) && isMobileTable) {
              return { style: { display: "none" } };
            }
            const colWidth = columnWidthFor(column.id);
            const iconColumn = column.id === "actions";
            const isCountryCol = isMobileTable && column.id === "location";
            const isCompanyCol = column.id === "company";
            const isDescriptionCol = column.id === "description";
            const mobilePad = isMobileTable ? mobileCellPadding(column.id) : {};
            return {
              align: iconColumn ? "center" : isCountryCol ? "right" : "left",
              style: {
                width: colWidth,
                minWidth: isCompanyCol ? COMPANY_COLUMN_MIN_WIDTH_PX : undefined,
                overflow: isCompanyCol ? "visible" : undefined,
                ...mobilePad,
                paddingLeft: isCompanyCol && !isMobileTable ? 14 : isDescriptionCol && !isMobileTable ? 4 : mobilePad.paddingLeft,
                paddingRight: isCompanyCol && !isMobileTable ? 12 : isDescriptionCol && !isMobileTable ? 14 : mobilePad.paddingRight,
                fontWeight: 600,
                fontSize: isMobileTable ? "var(--mantine-font-size-xs)" : "var(--mantine-font-size-sm)",
                textAlign: iconColumn ? "center" : isCountryCol ? "right" : "left",
                whiteSpace:
                  column.id === "actions" || column.id === "company"
                    ? "nowrap"
                    : !isMobileTable && column.id === "location"
                      ? "normal"
                      : undefined,
              },
            };
          }}
          mantineTableBodyCellProps={({ column }) => {
            if (MOBILE_HIDDEN_COLUMN_IDS.has(column.id) && isMobileTable) {
              return { style: { display: "none" } };
            }
            const colWidth = columnWidthFor(column.id);
            const locationDesktopWrap = !isMobileTable && column.id === "location";
            const wrapColumn =
              column.id === "description" || column.id === "industry" || locationDesktopWrap;
            const iconColumn = column.id === "actions";
            const cellPad = isMobileTable ? (column.id === "industry" ? 14 : 12) : 14;
            const industryWrap = isMobileTable && column.id === "industry";
            const isCountryCol = isMobileTable && column.id === "location";
            const isCompanyCol = column.id === "company";
            const isDescriptionCol = column.id === "description";
            const mobilePad = isMobileTable ? mobileCellPadding(column.id) : {};
            const isRowLinkHost = canViewCompanyDetails && isCompanyCol;
            return {
              align: iconColumn ? "center" : isCountryCol ? "right" : "left",
              ...(isRowLinkHost ? { "data-row-link-host": "" } : {}),
              ...(iconColumn ? { "data-action-cell": "" } : {}),
              style: {
                width: colWidth,
                minWidth: column.id === "description"
                  ? 0
                  : isCompanyCol
                    ? COMPANY_COLUMN_MIN_WIDTH_PX
                    : undefined,
                verticalAlign: iconColumn && !industryWrap ? "middle" : "top",
                textAlign: iconColumn ? "center" : isCountryCol ? "right" : "left",
                paddingTop: cellPad,
                paddingBottom: cellPad,
                paddingLeft:
                  isCompanyCol && !isMobileTable
                    ? 14
                    : isDescriptionCol && !isMobileTable
                      ? 4
                      : isMobileTable
                        ? mobilePad.paddingLeft
                        : undefined,
                paddingRight:
                  isCompanyCol && !isMobileTable
                    ? 12
                    : isDescriptionCol && !isMobileTable
                      ? 14
                      : isMobileTable
                        ? mobilePad.paddingRight
                        : undefined,
                overflow: industryWrap || isCompanyCol || locationDesktopWrap ? "visible" : undefined,
                ...(wrapColumn || industryWrap
                  ? { whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }
                  : isCompanyCol
                    ? { whiteSpace: "nowrap", overflow: "visible" }
                    : iconColumn
                      ? { whiteSpace: "nowrap", overflow: "visible" }
                      : { whiteSpace: "nowrap" }),
              },
            };
          }}
          mantineTableBodyRowProps={({ row }) => ({
            ...(canViewCompanyDetails ? { "data-row-link": "" } : {}),
            onClick: canViewCompanyDetails ? undefined : () => handleViewCompany(row.original.id),
            style: { cursor: "pointer", position: "relative" },
          })}
        />
      </Box>
      <ShortlistModal opened={signupModalOpen} onClose={() => setSignupModalOpen(false)} companyName={signupCompanyLabel} />
    </>
  );
}
